import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import ContactCard from '../components/ContactCard';
import { salesPhone, whatsappUrl } from '../utils/contactLinks';
import { company } from '../data/company';
import { trackEvent } from '../utils/analytics';
import { categories } from '../data/categories';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    product: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const location = useLocation();

  // Quote links from product pages pass ?product=<category>&item=<brand product> — pre-fill the form.
  // Done after mount (not in initial state) so the prerendered HTML still hydrates cleanly.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const product = params.get('product');
    const item = params.get('item');
    if (!product && !item) return;
    setFormData((prev) => ({
      ...prev,
      product: categories.some((c) => c.id === product) ? product : prev.product,
      message: prev.message || (item ? `Hi, I'd like a quote for ${item}.\n\nQuantity:\nDelivery area:\n` : ''),
    }));
  }, [location.search]);

  useEffect(() => {
    if (location.hash !== '#contact-form') return;
    const el = document.getElementById('contact-form');
    if (el) {
      const id = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [location.pathname, location.hash]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(`The contact service is not available right now. Please email us directly at ${company.email}`);
      }

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      // Extract email domain (privacy-compliant - no PII)
      const getEmailDomain = (email) => {
        if (!email || !email.includes('@')) return 'unknown';
        //return domain if split returnarray
        const domain = email.split('@');
        if (domain.length > 0) {
          return domain[1];
        }
        return 'unknown';
      };

      // Extract phone country code (privacy-compliant - no actual number)
      const getPhoneMetadata = (phone) => {
        if (!phone) return { has_phone: false, country_code: null };
        const cleaned = phone.replace(/\D/g, '');
        // Check for South African number (+27 or 0 prefix)
        if (cleaned.startsWith('27') || cleaned.startsWith('0')) {
          return { has_phone: true, country_code: 'ZA' };
        }
        return { has_phone: true, country_code: 'other' };
      };

      const phoneMeta = getPhoneMetadata(formData.phone);
      const emailDomain = getEmailDomain(formData.email);

      // GA4 recommended lead event. Not "form_submit": enhanced measurement already sends that name.
      trackEvent('generate_lead', {
        form_id: 'contact_form',
        product_interest: formData.product || 'general',
        has_phone: phoneMeta.has_phone,
        phone_country_code: phoneMeta.country_code || 'none',
        email_domain: emailDomain,
      });

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', product: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);

      trackEvent('form_submit_error', {
        form_id: 'contact_form',
        error_message: err.message.substring(0, 100),
      });
    }
  };

  return (
    <div className="contact-page">
      <SEO />

      <PageHero
        breadcrumbs={[{ name: 'Contact Us', path: '/contact' }]}
        kicker="Showroom in Joubert Industria, George"
        bg="/images/bg-shopfront.jpg"
        title="Contact Our George Showroom"
        text="Visit our showroom or contact us for expert advice on your project requirements. We're here to help you find the perfect solutions."
      >
        <div className="contact-quick" data-track-location="contact_quick">
          <a href={salesPhone.href}>
            <Icon name="phone" size={22} />
            <span>
              <small>Call sales</small>
              {salesPhone.number}
            </span>
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="whatsapp" size={22} />
            <span>
              <small>WhatsApp</small>
              Chat with us
            </span>
          </a>
          <a href={`mailto:${company.email}`}>
            <Icon name="mail" size={22} />
            <span>
              <small>Email</small>
              {company.email}
            </span>
          </a>
        </div>
      </PageHero>

      <section className="section section-light">
        <div className="container contact-grid">
          {/* Contact Form */}
          <div id="contact-form" className="contact-form-wrapper">
            <h2>Send us a message</h2>
            <p className="form-intro">
              Tell us what you need — products, quantities and delivery area — and we&apos;ll get back to you as soon as
              possible.
            </p>

            {status === 'success' && (
              <div className="form-status form-status-success" role="status">
                Thank you for your inquiry! We&apos;ll get back to you as soon as possible.
              </div>
            )}
            {status === 'error' && (
              <div className="form-status form-status-error" role="alert">
                {errorMsg || 'Something went wrong. Please try again.'}
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    placeholder="Your name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    placeholder="064 XXX XXXX"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="your@email.com"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="product" className="form-label">Product Interest</label>
                  <select
                    id="product"
                    name="product"
                    className="form-select"
                    value={formData.product}
                    onChange={handleChange}
                  >
                    <option value="">Select a product category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                    <option value="general">General Inquiry</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  className="form-textarea"
                  placeholder="Tell us about your project requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className="btn btn-primary btn-large btn-block" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact details */}
          <div className="contact-side">
            <ContactCard title="Contact information" showQuote={false} detailed />
          </div>
        </div>

        <div className="container">
          <div className="visit-us-card">
            <h3>Why visit our showroom?</h3>
            <ul className="check-list">
              <li>Expert staff ready to assist with product selection</li>
              <li>View samples and compare materials in person</li>
              <li>Get immediate quotes and availability</li>
              <li>Pick up materials same day for urgent projects</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="map-section">
        <iframe
          title={`${company.name} location in Joubert Industria, George`}
          src={company.mapsEmbed}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </div>
  );
}

export default Contact;
